import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Layout } from '@/components/Layout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import axios from 'axios';
import { 
  Car, 
  Search, 
  Loader2, 
  User,
  Calendar,
  Filter,
  ChevronRight,
  Trophy,
  ThumbsUp,
  ArrowLeft,
  Images
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function MemberVehiclesPage() {
  const [loading, setLoading] = useState(true);
  const [vehicles, setVehicles] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedOwner, setSelectedOwner] = useState(null);
  const [likeCounts, setLikeCounts] = useState({});
  const [myLikes, setMyLikes] = useState(new Set());

  const getToken = () => localStorage.getItem('shrt_token');

  useEffect(() => {
    fetchAllVehicles();
    fetchLikes();
  }, []);

  const fetchAllVehicles = async () => {
    try {
      const token = getToken();
      const response = await axios.get(
        `${API_URL}/api/members/vehicles/all`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setVehicles(response.data.vehicles || []);
    } catch (error) {
      console.error('Error fetching vehicles:', error);
      toast.error('Fehler beim Laden der Fahrzeuge');
    } finally {
      setLoading(false);
    }
  };

  const fetchLikes = async () => {
    try {
      const token = getToken();
      const response = await axios.get(`${API_URL}/api/vehicles/likes`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setLikeCounts(response.data.counts || {});
      setMyLikes(new Set(response.data.my_likes || []));
    } catch (error) {
      console.error('Error fetching likes:', error);
    }
  };

  const toggleLike = async (e, vehicleId) => {
    e.stopPropagation();
    e.preventDefault();
    try {
      const token = getToken();
      const response = await axios.post(`${API_URL}/api/vehicles/${vehicleId}/like`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setLikeCounts(prev => ({ ...prev, [vehicleId]: response.data.count }));
      setMyLikes(prev => {
        const next = new Set(prev);
        response.data.liked ? next.add(vehicleId) : next.delete(vehicleId);
        return next;
      });
    } catch (error) {
      toast.error('Fehler beim Liken');
    }
  };

  // Group vehicles by owner
  const ownerGroups = vehicles.reduce((acc, vehicle) => {
    if (!acc[vehicle.owner_id]) {
      acc[vehicle.owner_id] = {
        owner_name: vehicle.owner_name,
        owner_id: vehicle.owner_id,
        vehicles: []
      };
    }
    acc[vehicle.owner_id].vehicles.push(vehicle);
    return acc;
  }, {});

  const sortedGroups = Object.values(ownerGroups).sort((a, b) => a.owner_name.localeCompare(b.owner_name));

  // Filter groups by search
  const filteredGroups = searchTerm
    ? sortedGroups.filter(g => {
        const term = searchTerm.toLowerCase();
        return g.owner_name.toLowerCase().includes(term) ||
          g.vehicles.some(v => 
            v.vehicle_name.toLowerCase().includes(term) ||
            (v.vehicle_year && v.vehicle_year.includes(term))
          );
      })
    : sortedGroups;

  // Get selected group
  const activeGroup = selectedOwner ? ownerGroups[selectedOwner] : null;

  // Total likes for a group
  const groupLikeTotal = (group) => 
    group.vehicles.reduce((sum, v) => sum + (likeCounts[v.vehicle_id] || 0), 0);

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center min-h-[400px]">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-8">
          <div>
            <h1 className="font-['Oswald'] text-3xl font-bold uppercase tracking-wide flex items-center gap-3">
              <Car className="text-primary" />
              Fahrzeuge der Mitglieder
            </h1>
            <p className="text-muted-foreground mt-1">
              {vehicles.length} Fahrzeuge von {sortedGroups.length} Mitgliedern
            </p>
          </div>
          
          <Link to="/car-of-the-month">
            <Button className="rounded-none uppercase tracking-wider">
              <Trophy className="w-4 h-4 mr-2" />
              Auto des Monats
            </Button>
          </Link>
        </div>

        {/* Back button when viewing a member's vehicles */}
        {selectedOwner && activeGroup && (
          <button
            onClick={() => setSelectedOwner(null)}
            className="flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors mb-6"
            data-testid="back-to-stacks"
          >
            <ArrowLeft className="w-4 h-4" />
            Alle Mitglieder
          </button>
        )}

        {/* Search (only in stack view) */}
        {!selectedOwner && (
          <div className="bg-card border border-border p-4 mb-6">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Suche nach Fahrzeug, Besitzer oder Jahr..."
                className="pl-10 rounded-none"
                data-testid="vehicle-search"
              />
            </div>
          </div>
        )}

        {/* =================== STACK VIEW (default) =================== */}
        {!selectedOwner && (
          <>
            {filteredGroups.length === 0 && (
              <div className="text-center py-12 bg-card border border-border">
                <Car className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                <p className="text-lg text-muted-foreground">Keine Fahrzeuge gefunden</p>
                {searchTerm && (
                  <Button variant="link" onClick={() => setSearchTerm('')} className="mt-2">
                    Filter zurücksetzen
                  </Button>
                )}
              </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {filteredGroups.map((group) => (
                <OwnerStack
                  key={group.owner_id}
                  group={group}
                  totalLikes={groupLikeTotal(group)}
                  onClick={() => setSelectedOwner(group.owner_id)}
                />
              ))}
            </div>
          </>
        )}

        {/* =================== DETAIL VIEW (selected member) =================== */}
        {selectedOwner && activeGroup && (
          <>
            {/* Member header */}
            <div className="bg-card border border-border p-5 mb-6 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-primary/20 rounded-full flex items-center justify-center flex-shrink-0">
                  <User className="w-6 h-6 text-primary" />
                </div>
                <div>
                  <h2 className="font-['Oswald'] text-xl font-bold uppercase tracking-wide">
                    {activeGroup.owner_name}
                  </h2>
                  <p className="text-sm text-muted-foreground">
                    {activeGroup.vehicles.length} {activeGroup.vehicles.length === 1 ? 'Fahrzeug' : 'Fahrzeuge'}
                  </p>
                </div>
              </div>
              <Link to={`/members/${activeGroup.owner_id}`}>
                <Button variant="outline" size="sm" className="rounded-none">
                  Profil ansehen
                  <ChevronRight className="w-4 h-4 ml-1" />
                </Button>
              </Link>
            </div>

            {/* Vehicle cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {activeGroup.vehicles.map((vehicle) => (
                <Link
                  key={vehicle.vehicle_id}
                  to={`/fahrzeug/${vehicle.owner_id}/${vehicle.vehicle_id}`}
                  className="bg-card border border-border overflow-hidden hover:border-primary/50 transition-colors group flex flex-col"
                  data-testid={`vehicle-card-${vehicle.vehicle_id}`}
                >
                  {/* Image */}
                  <div className="relative">
                    {vehicle.images && vehicle.images.length > 0 ? (
                      <div className="aspect-[4/3] overflow-hidden">
                        <img
                          src={vehicle.images[0].url}
                          alt={vehicle.vehicle_name}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                        />
                      </div>
                    ) : (
                      <div className="aspect-[4/3] bg-muted/30 flex items-center justify-center">
                        <Car className="w-16 h-16 text-muted-foreground/30" />
                      </div>
                    )}
                    {vehicle.images && vehicle.images.length > 1 && (
                      <span className="absolute top-2 right-2 text-xs text-white bg-black/60 px-2 py-0.5 flex items-center gap-1">
                        <Images size={12} /> {vehicle.images.length}
                      </span>
                    )}
                  </div>

                  {/* Info */}
                  <div className="p-4 flex-1">
                    <h3 className="font-semibold text-foreground truncate">{vehicle.vehicle_name}</h3>
                    {vehicle.vehicle_year && (
                      <p className="text-sm text-primary flex items-center gap-1 mt-0.5">
                        <Calendar className="w-3 h-3" />
                        {vehicle.vehicle_year}
                      </p>
                    )}
                    {vehicle.vehicle_description && (
                      <p className="text-sm text-muted-foreground mt-2 line-clamp-2">
                        {vehicle.vehicle_description}
                      </p>
                    )}
                  </div>

                  {/* Footer with like */}
                  <div className="flex items-center justify-between px-4 py-3 border-t border-border mt-auto">
                    <span className="text-xs text-muted-foreground">
                      {vehicle.vehicle_year || ''}
                    </span>
                    <button
                      onClick={(e) => toggleLike(e, vehicle.vehicle_id)}
                      className={`flex items-center gap-1.5 px-2.5 py-1 text-sm transition-all border flex-shrink-0 ${myLikes.has(vehicle.vehicle_id) ? 'border-primary bg-primary/10 text-primary' : 'border-border text-muted-foreground hover:border-primary/50 hover:text-primary'}`}
                      data-testid={`like-btn-${vehicle.vehicle_id}`}
                    >
                      <ThumbsUp size={14} className={myLikes.has(vehicle.vehicle_id) ? 'fill-primary' : ''} />
                      {(likeCounts[vehicle.vehicle_id] || 0) > 0 && <span className="font-medium">{likeCounts[vehicle.vehicle_id]}</span>}
                    </button>
                  </div>
                </Link>
              ))}
            </div>
          </>
        )}

        {/* Add Your Vehicle CTA */}
        {!selectedOwner && (
          <div className="mt-12 text-center p-8 bg-gradient-to-r from-primary/10 to-primary/5 border border-primary/20">
            <Car className="w-12 h-12 text-primary mx-auto mb-4" />
            <h2 className="font-['Oswald'] text-xl font-bold uppercase tracking-wide mb-2">
              Zeigen Sie Ihre Fahrzeuge
            </h2>
            <p className="text-muted-foreground mb-4">
              Fügen Sie Ihre historischen Fahrzeuge hinzu und teilen Sie Ihre Leidenschaft mit anderen Mitgliedern.
            </p>
            <Link to="/vehicles">
              <Button className="rounded-none uppercase tracking-wider">
                Meine Fahrzeuge verwalten
              </Button>
            </Link>
          </div>
        )}
      </div>
    </Layout>
  );
}


/* =================== Fan Stack Component =================== */
function OwnerStack({ group, totalLikes, onClick }) {
  const { vehicles, owner_name } = group;
  const count = vehicles.length;
  // Get up to 3 images for the stack
  const stackImages = vehicles
    .filter(v => v.images && v.images.length > 0)
    .slice(0, 3)
    .map(v => v.images[0].url);

  // Fan rotations for stacked cards
  const fanStyles = [
    { zIndex: 30, rotate: 0, x: 0, y: 0 },         // front
    { zIndex: 20, rotate: 4.5, x: 12, y: -6 },     // middle
    { zIndex: 10, rotate: -3.5, x: -10, y: -3 },   // back
  ];

  return (
    <button
      onClick={onClick}
      className="text-left group focus:outline-none"
      data-testid={`owner-stack-${group.owner_id}`}
    >
      {/* Stack of images */}
      <div className="relative mb-3" style={{ paddingBottom: '75%' }}>
        {stackImages.length === 0 && (
          <div className="absolute inset-0 bg-muted/30 border border-border flex items-center justify-center">
            <Car className="w-16 h-16 text-muted-foreground/30" />
          </div>
        )}
        {stackImages.map((url, i) => {
          const style = fanStyles[i] || fanStyles[0];
          const isTop = i === 0;
          return (
            <div
              key={i}
              className={`absolute inset-0 border border-border overflow-hidden bg-card shadow-md transition-transform duration-300 ${isTop ? 'group-hover:scale-[1.02]' : ''}`}
              style={{
                zIndex: style.zIndex,
                transform: `rotate(${style.rotate}deg) translate(${style.x}px, ${style.y}px)`,
              }}
            >
              <img
                src={url}
                alt=""
                className="w-full h-full object-cover"
                draggable={false}
              />
            </div>
          );
        })}

        {/* Vehicle count badge */}
        {count > 1 && (
          <div className="absolute top-2 right-2 z-40 bg-black/70 text-white text-xs font-medium px-2 py-0.5 flex items-center gap-1">
            <Car size={12} /> {count}
          </div>
        )}
      </div>

      {/* Info below the stack */}
      <div className="bg-card border border-border px-4 py-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2 min-w-0">
            <User className="w-4 h-4 text-primary flex-shrink-0" />
            <span className="font-semibold text-foreground truncate">{owner_name}</span>
          </div>
          <ChevronRight className="w-4 h-4 text-muted-foreground flex-shrink-0 group-hover:text-primary transition-colors" />
        </div>
        <div className="flex items-center justify-between mt-1.5 text-xs text-muted-foreground">
          <span>{count} {count === 1 ? 'Fahrzeug' : 'Fahrzeuge'}</span>
          {totalLikes > 0 && (
            <span className="flex items-center gap-1">
              <ThumbsUp size={11} /> {totalLikes}
            </span>
          )}
        </div>
      </div>
    </button>
  );
}
